import { Component, ElementRef, input, output, signal, viewChild } from "@angular/core";
import { TranslatePipe } from "@ngx-translate/core";

import { AdaaHelper } from "../../../core/utils";
import { AdaaBoolean, Language, PageMode } from "../../constants/enums";
import { UserRolesPermission } from "../../models";

type checkType = "create" | "view" | "manage" | "execute" | "dataEntry" | "null";

@Component({
  selector: "adaa-user-roles-manager",
  imports: [TranslatePipe],
  templateUrl: "./user-roles-manager.html",
  styleUrl: "./user-roles-manager.scss",
})
export class UserRolesManagerComponent {
  Language = Language;
  AdaaHelper = AdaaHelper;
  AdaaBoolean = AdaaBoolean;
  PageMode = PageMode;
  data = input<UserRolesPermission[]>();
  isPmo = input<boolean>();
  pageMode = input<string>();
  setPermissions = output<UserRolesPermission>();
  selectAll = output<{ event: Event; value: string }>();
  showHighlight: boolean = false;
  highlightText = signal<{
    itemTypeDescAE?: string;
    itemTypeDescEN?: string;
    itemTypeDescURLs?: string;
  }>({
    itemTypeDescAE: "",
    itemTypeDescEN: "",
    itemTypeDescURLs: "",
  });

  generalTabState = signal<{
    create: boolean;
    view: boolean;
    manage: boolean;
    execute: boolean;
    dataEntry: boolean;
  }>({
    create: false,
    view: false,
    manage: false,
    execute: false,
    dataEntry: false,
  });
  pmoTabState = signal<{
    create: boolean;
    view: boolean;
    manage: boolean;
    execute: boolean;
    dataEntry: boolean;
  }>({
    create: false,
    view: false,
    manage: false,
    execute: false,
    dataEntry: false,
  });
  viewCheckbox = viewChild.required<ElementRef<HTMLInputElement>>("view");
  manageCheckbox = viewChild.required<ElementRef<HTMLInputElement>>("manage");

  public updateState(event: Event, value: checkType) {
    this.selectAll.emit({ event, value });
    const element = event.target as HTMLInputElement;
    const checked = element.checked;
    const tab = this.isPmo() ? this.pmoTabState : this.generalTabState;

    switch (value) {
      case "create":
        tab.update((value) => ({ ...value, create: checked }));
        this.viewCheckbox().nativeElement.checked = checked;
        this.manageCheckbox().nativeElement.checked = checked;
        break;
      case "view":
        tab.update((value) => ({ ...value, view: checked }));
        break;
      case "manage":
        tab.update((value) => ({ ...value, manage: checked }));
        this.viewCheckbox().nativeElement.checked = checked;
        break;
      case "execute":
        tab.update((value) => ({ ...value, execute: checked }));
        break;
      case "dataEntry":
        tab.update((value) => ({ ...value, dataEntry: checked }));
        break;

      default:
        break;
    }
  }

  public valueChanged(event: Event, id: number, permission: string): void {
    const target = event.target as HTMLInputElement;
    const isChecked = target.checked;
    const value = this.data()?.find((item) => item.id === id);
    if (value) {
      switch (permission) {
        case "create":
          value.create = isChecked ? AdaaBoolean.Y : AdaaBoolean.N;
          if (isChecked && value.viewEnabled === AdaaBoolean.Y && value.manageEnabled === AdaaBoolean.Y) {
            value.view = AdaaBoolean.Y;
            value.manage = AdaaBoolean.Y;
          }
          break;
        case "view":
          value.view = isChecked ? AdaaBoolean.Y : AdaaBoolean.N;
          if (!isChecked) {
            value.create = AdaaBoolean.N;
            value.manage = AdaaBoolean.N;
          }
          break;
        case "manage":
          value.manage = isChecked ? AdaaBoolean.Y : AdaaBoolean.N;
          if (isChecked) {
            if (value.viewEnabled === AdaaBoolean.Y) value.view = AdaaBoolean.Y;
          } else {
            value.create = AdaaBoolean.N;
          }
          break;
        case "execute":
          value.execute = isChecked ? AdaaBoolean.Y : AdaaBoolean.N;
          break;
        case "dataEntry":
          value.dataEntry = isChecked ? AdaaBoolean.Y : AdaaBoolean.N;
          break;

        default:
          break;
      }

      this.setPermissions.emit(value);
    }
  }

  public getHighlight(itemId: number) {
    const itemType = this.data()?.find((permission) => permission.itemTypeId === itemId);
    if (itemType?.itemTypeDescAE && itemType.itemTypeDescEN) {
      this.highlightText.set({
        itemTypeDescAE: itemType.itemTypeDescAE ?? "",
        itemTypeDescEN: itemType.itemTypeDescEN ?? "",
        itemTypeDescURLs: itemType.itemTypeDescURLs ?? "",
      });
    }
  }
}
